// src/pages/student/AssignedSupervisor.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Badge,
    CardActions,
    Stack,
    useMediaQuery,
    Tab,
    Tabs,
    LinearProgress,
    Rating,
    AvatarGroup,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Person,
    Email,
    Phone,
    CheckCircle,
    Warning,
    Schedule,
    TrendingUp,
    School,
    Verified,
    Pending,
    CalendarToday,
    Feedback,
    People,
    Flag,
    PriorityHigh,
    VideoCall,
    Send,
    Visibility,
    Close,
    Assignment,
    Chat,
    DoneAll,
    Done,
    SupervisorAccount,
    Work,
    Badge as BadgeIcon,
    Star,
    StarBorder,
    AccessTime,
    LocationOn,
    Language,
    LinkedIn,
    GitHub,
    Twitter,
    Description,
    Article,
    Science,
    MenuBook,
    Psychology,
    EmojiEvents,
    Timeline as TimelineIcon,
    Assessment,
    TrendingUp as TrendingUpIcon,
    ThumbUp,
    ThumbUpAlt,
    Message,
    WhatsApp,
    Instagram,
    Facebook
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIStudentProfileImage,
    APIAssignStudentByStudent,
    APIAssignStudentMessages,
    APIAssignStudentSendMessage,
    APIAssignStudentMarkRead,
    APIAssignStudentUnreadCount,
    APISupervisorProfileImage,
    APISupervisorProfileById
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'SP';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const AssignedSupervisor = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [supervisorDetails, setSupervisorDetails] = useState({});
    const [supervisorLoading, setSupervisorLoading] = useState(false);
    
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        // Auto-refresh every 2 minutes
        const interval = setInterval(() => {
            fetchAllData(true);
        }, 120000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        
        try {
            await fetchAssignments();
        } catch (error) {
            if (!silent) {
                setError("Failed to load supervisor data");
                console.error("Fetch error:", error);
            }
        } finally {
            if (!silent && isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchAssignments = async () => {
        try {
            const result = await apiService.listById(APIAssignStudentByStudent, studentId);
            if (result.success && isMounted.current) {
                const assignmentsData = Array.isArray(result.data) ? result.data : [];
                setAssignments(assignmentsData);
                
                // Fetch supervisor details for each assignment
                for (const assignment of assignmentsData) {
                    if (assignment.supervisor?.id) {
                        await fetchSupervisorDetails(assignment.supervisor.id);
                    }
                }
                
                // Fetch unread counts
                await fetchUnreadCounts();
            } else {
                console.error("Failed to fetch assignments:", result?.message);
            }
        } catch (error) {
            console.error("Error fetching assignments:", error);
        }
    };

    const fetchSupervisorDetails = async (supervisorId) => {
        if (supervisorDetails[supervisorId]) return;
        
        setSupervisorLoading(true);
        try {
            const result = await apiService.show(APISupervisorProfileById, supervisorId);
            if (result.success && result.data && isMounted.current) {
                setSupervisorDetails(prev => ({
                    ...prev,
                    [supervisorId]: result.data
                }));
            }
        } catch (error) {
            console.error("Error fetching supervisor details:", error);
        } finally {
            setSupervisorLoading(false);
        }
    };

    const fetchUnreadCounts = async () => {
        try {
            const result = await apiService.listById(APIAssignStudentUnreadCount, `${studentId}/student`);
            if (result.success && result.data) {
                setUnreadCounts({ [selectedAssignment?.id]: result.data.unread_count });
            }
        } catch (error) {
            console.error("Error fetching unread counts:", error);
        }
    };

    const fetchMessages = async (assignmentId) => {
        try {
            const result = await apiService.listById(APIAssignStudentMessages, assignmentId, {
                user_id: studentId,
                user_type: 'student'
            });
            if (result.success && isMounted.current) {
                setMessages(Array.isArray(result.data) ? result.data : []);
                // Mark as read
                const markFormData = new FormData();
                markFormData.append('assignment_id', assignmentId);
                markFormData.append('user_id', studentId);
                markFormData.append('user_type', 'student');
                await apiService.update(APIAssignStudentMarkRead, markFormData);
                // Update unread count
                await fetchUnreadCounts();
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedAssignment) return;
        
        setSendingMessage(true);
        try {
            const formData = new FormData();
            formData.append('assignment_id', selectedAssignment.id);
            formData.append('sender_id', studentId);
            formData.append('sender_type', 'student');
            formData.append('receiver_id', selectedAssignment.supervisor.id);
            formData.append('receiver_type', 'supervisor');
            formData.append('message', newMessage);
            
            const result = await apiService.create(APIAssignStudentSendMessage, formData);
            
            if (result && result.success) {
                setMessages(prev => [...prev, result.data]);
                setNewMessage('');
                // Scroll to bottom
                setTimeout(() => {
                    const chatContainer = document.getElementById('chat-messages');
                    if (chatContainer) {
                        chatContainer.scrollTop = chatContainer.scrollHeight;
                    }
                }, 100);
            } else {
                showToast("Error", result?.message || "Failed to send message", "error");
            }
        } catch (error) {
            console.error("Send message error:", error);
            showToast("Error", error.response?.data?.message || "Failed to send message", "error");
        } finally {
            setSendingMessage(false);
        }
    };

    const handleOpenViewDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedAssignment(null);
    };

    const handleOpenChatDialog = async (assignment) => {
        setSelectedAssignment(assignment);
        setOpenChatDialog(true);
        await fetchMessages(assignment.id);
    };

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
        setSelectedAssignment(null);
        setMessages([]);
        setNewMessage('');
    };

    const getProfileImageUrl = (user, type = 'student') => {
        if (type === 'supervisor' && user?.profile_image_path) {
            return getImageUrl(APISupervisorProfileImage, user.profile_image_path);
        }
        if (user?.profile_image) {
            return getImageUrl(APIStudentProfileImage, user.profile_image);
        }
        return null;
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return 'success';
            case 'completed': return 'info';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch(status) {
            case 'active': return 'Active';
            case 'completed': return 'Completed';
            case 'pending': return 'Pending';
            case 'cancelled': return 'Cancelled';
            default: return status || 'Active';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'active': return <Verified sx={{ fontSize: 16 }} />;
            case 'completed': return <CheckCircle sx={{ fontSize: 16 }} />;
            case 'pending': return <Pending sx={{ fontSize: 16 }} />;
            default: return <Schedule sx={{ fontSize: 16 }} />;
        }
    };

    const filteredAssignments = () => {
        if (activeTab === 0) return assignments;
        if (activeTab === 1) return assignments.filter(a => a.status === 'active');
        if (activeTab === 2) return assignments.filter(a => a.status === 'pending');
        if (activeTab === 3) return assignments.filter(a => a.status === 'completed');
        return assignments;
    };

    // Calculate statistics
    const stats = {
        total: assignments.length,
        active: assignments.filter(a => a.status === 'active').length,
        pending: assignments.filter(a => a.status === 'pending').length,
        completed: assignments.filter(a => a.status === 'completed').length
    };

    if (loading) {
        return <LoadingState message="Loading Assigned Supervisor..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Supervisor Data" variant="research" fullPage retryText="Retry Loading" />;
    }

    const displayAssignments = filteredAssignments();

    return (
        <>
            <FullWidthContainer >
                <StudentBar notificationCount={5} />
                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <SupervisorAccount /> My Supervisor ({assignments.length})
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="info" 
                            startIcon={<RefreshIcon />} 
                            onClick={() => fetchAllData()} 
                            sx={{ 
                                textTransform: 'none', 
                                borderRadius: '8px',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.primary.light + '10',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.total}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Total Assignments
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.success.light + '10',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.active}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Active
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.warning.light + '10',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.pending}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Pending
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.info.light + '10',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.completed}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Completed
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
                
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label="All" />
                        <Tab label="Active" />
                        <Tab label="Pending" />
                        <Tab label="Completed" />
                    </Tabs>
                </Paper>

                {/* Supervisor Cards Grid */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {displayAssignments.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <SupervisorAccount sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No Supervisor Assigned
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                A supervisor will be assigned to you soon
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {displayAssignments.map((assignment, index) => {
                                const supervisor = assignment.supervisor;
                                const supervisorDetail = supervisorDetails[supervisor?.id];
                                const avatarColor = stringToColor(supervisor?.name);
                                const initials = getInitials(supervisor?.name);
                                const profileImage = getProfileImageUrl(supervisor, 'supervisor');
                                
                                return (
                                    <Grid item xs={12} lg={6} key={assignment.id}>
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: theme.shadows[8]
                                                },
                                                borderRadius: 3,
                                                overflow: 'hidden'
                                            }}>
                                                {/* Header with gradient */}
                                                <Box sx={{ 
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                    p: 3,
                                                    color: 'white'
                                                }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'flex-start',
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: 2
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar 
                                                                src={profileImage}
                                                                sx={{ 
                                                                    width: { xs: 64, sm: 80 }, 
                                                                    height: { xs: 64, sm: 80 },
                                                                    bgcolor: !profileImage ? avatarColor : 'transparent',
                                                                    fontWeight: 'bold',
                                                                    border: '3px solid white',
                                                                    fontSize: '1.5rem'
                                                                }}
                                                            >
                                                                {!profileImage && initials}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                    {supervisor?.name}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Work sx={{ fontSize: 14 }} />
                                                                    {supervisorDetail?.academic_title || 'Research Supervisor'}
                                                                </Typography>
                                                                {supervisor?.email && (
                                                                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                        <Email sx={{ fontSize: 12 }} />
                                                                        {supervisor?.email}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Chip 
                                                            icon={getStatusIcon(assignment.status)}
                                                            label={getStatusLabel(assignment.status)} 
                                                            size="medium" 
                                                            color={getStatusColor(assignment.status)}
                                                            sx={{ 
                                                                fontWeight: 'bold',
                                                                backgroundColor: 'rgba(255,255,255,0.9)',
                                                                '& .MuiChip-icon': { color: 'inherit' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                    {/* Expertise & Research Areas */}
                                                    {supervisorDetail?.expertise && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ 
                                                                fontWeight: 'bold', 
                                                                color: theme.palette.primary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 1
                                                            }}>
                                                                <Science fontSize="small" />
                                                                Research Expertise
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {supervisorDetail.expertise.split(',').map((exp, i) => (
                                                                    <Chip 
                                                                        key={i}
                                                                        label={exp.trim()}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontSize: '0.7rem' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Assignment Details */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ 
                                                            fontWeight: 'bold', 
                                                            color: theme.palette.primary.main,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mb: 1
                                                        }}>
                                                            <Assignment fontSize="small" />
                                                            Assignment Details
                                                        </Typography>
                                                        <Stack spacing={1}>
                                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <BadgeIcon fontSize="small" color="action" />
                                                                <strong>Assignment ID:</strong> {assignment.assignment_id}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CalendarToday fontSize="small" color="action" />
                                                                <strong>Assigned Date:</strong> {formatCreatedAtDate(assignment.assigned_date)}
                                                            </Typography>
                                                            {assignment.completion_date && (
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CheckCircle fontSize="small" color="success" />
                                                                    <strong>Completion Date:</strong> {formatCreatedAtDate(assignment.completion_date)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>

                                                    {/* Contact Information */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ 
                                                            fontWeight: 'bold', 
                                                            color: theme.palette.primary.main,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mb: 1
                                                        }}>
                                                            <Phone fontSize="small" />
                                                            Contact Information
                                                        </Typography>
                                                        <Stack spacing={1}>
                                                            {supervisor?.contact_phone && (
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Phone fontSize="small" color="action" />
                                                                    {supervisor?.contact_phone}
                                                                </Typography>
                                                            )}
                                                            {supervisorDetail?.office_location && (
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <LocationOn fontSize="small" color="action" />
                                                                    {supervisorDetail.office_location}
                                                                </Typography>
                                                            )}
                                                            {supervisorDetail?.consultation_hours && (
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <AccessTime fontSize="small" color="action" />
                                                                    Consultation: {supervisorDetail.consultation_hours}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>

                                                    {/* Assignment Notes */}
                                                    {assignment.assignment_notes && (
                                                        <Paper sx={{ 
                                                            p: 2, 
                                                            bgcolor: theme.palette.grey[50], 
                                                            borderRadius: 2,
                                                            mb: 2
                                                        }}>
                                                            <Typography variant="subtitle2" sx={{ 
                                                                fontWeight: 'bold', 
                                                                mb: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1
                                                            }}>
                                                                <Feedback fontSize="small" />
                                                                Supervisor Notes
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                {assignment.assignment_notes}
                                                            </Typography>
                                                        </Paper>
                                                    )}

                                                    {/* Progress Timeline */}
                                                    {assignment.status === 'active' && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ 
                                                                fontWeight: 'bold', 
                                                                color: theme.palette.primary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 1
                                                            }}>
                                                                <TimelineIcon fontSize="small" />
                                                                Supervision Progress
                                                            </Typography>
                                                            <Stepper activeStep={1} orientation="vertical" sx={{ mt: 1 }}>
                                                                <Step active>
                                                                    <StepLabel>Assignment Initiated</StepLabel>
                                                                </Step>
                                                                <Step active={assignment.status === 'active'}>
                                                                    <StepLabel>Active Supervision</StepLabel>
                                                                </Step>
                                                                <Step active={false}>
                                                                    <StepLabel>Completion</StepLabel>
                                                                </Step>
                                                            </Stepper>
                                                        </Box>
                                                    )}
                                                </CardContent>

                                                <CardActions sx={{ 
                                                    p: 3, 
                                                    pt: 0, 
                                                    gap: 1, 
                                                    flexWrap: 'wrap',
                                                    justifyContent: { xs: 'center', sm: 'flex-start' },
                                                    borderTop: `1px solid ${theme.palette.divider}`,
                                                    mt: 2
                                                }}>
                                                    <Tooltip title="Send Message">
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<Chat />}
                                                            onClick={() => handleOpenChatDialog(assignment)}
                                                            sx={{ 
                                                                textTransform: 'none',
                                                                borderRadius: 2,
                                                                flex: { xs: 1, sm: 'auto' }
                                                            }}
                                                        >
                                                            Message
                                                            {unreadCounts[assignment.id] > 0 && (
                                                                <Badge 
                                                                    badgeContent={unreadCounts[assignment.id]} 
                                                                    color="error"
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            )}
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="View Details">
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Visibility />}
                                                            onClick={() => handleOpenViewDialog(assignment)}
                                                            sx={{ 
                                                                textTransform: 'none',
                                                                borderRadius: 2,
                                                                flex: { xs: 1, sm: 'auto' }
                                                            }}
                                                        >
                                                            Details
                                                        </Button>
                                                    </Tooltip>
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </motion.div>

                {/* View Details Dialog - Enhanced */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh',
                            borderRadius: 3
                        }
                    }}
                >
                    {selectedAssignment && selectedAssignment.supervisor && (
                        <>
                            <DialogTitle sx={{ 
                                fontSize: { xs: '1rem', sm: '1.25rem' }, 
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SupervisorAccount />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Supervisor Details
                                        </Typography>
                                    </Box>
                                    <Chip 
                                        icon={getStatusIcon(selectedAssignment.status)}
                                        label={getStatusLabel(selectedAssignment.status)} 
                                        color={getStatusColor(selectedAssignment.status)}
                                        size="small"
                                        sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                    />
                                </Box>
                            </DialogTitle>
                            <DialogContent>
                                <Box sx={{ pt: 2 }}>
                                    {/* Profile Header */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 3, 
                                        mb: 4,
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        <Avatar 
                                            src={getProfileImageUrl(selectedAssignment.supervisor, 'supervisor')}
                                            sx={{ 
                                                width: { xs: 100, sm: 120 }, 
                                                height: { xs: 100, sm: 120 },
                                                bgcolor: !getProfileImageUrl(selectedAssignment.supervisor, 'supervisor') 
                                                    ? stringToColor(selectedAssignment.supervisor.name) 
                                                    : 'transparent',
                                                fontSize: '3rem',
                                                fontWeight: 'bold',
                                                border: `4px solid ${theme.palette.primary.main}`
                                            }}
                                        >
                                            {!getProfileImageUrl(selectedAssignment.supervisor, 'supervisor') && 
                                                getInitials(selectedAssignment.supervisor.name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {selectedAssignment.supervisor.name}
                                            </Typography>
                                            <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.academic_title || 'Research Supervisor'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                                <Email fontSize="small" />
                                                {selectedAssignment.supervisor.email}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    <Grid container spacing={3}>
                                        {/* Personal Information */}
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main, 
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Person fontSize="small" />
                                                Personal Information
                                            </Typography>
                                            
                                            <Stack spacing={2}>
                                                {selectedAssignment.supervisor.contact_phone && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Phone fontSize="small" color="action" />
                                                        <strong>Phone:</strong> {selectedAssignment.supervisor.contact_phone}
                                                    </Typography>
                                                )}
                                                {selectedAssignment.supervisor.nationality && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Flag fontSize="small" color="action" />
                                                        <strong>Nationality:</strong> {selectedAssignment.supervisor.nationality}
                                                    </Typography>
                                                )}
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.department && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <School fontSize="small" color="action" />
                                                        <strong>Department:</strong> {supervisorDetails[selectedAssignment.supervisor.id].department}
                                                    </Typography>
                                                )}
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.university_name && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <School fontSize="small" color="action" />
                                                        <strong>University:</strong> {supervisorDetails[selectedAssignment.supervisor.id].university_name}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Grid>

                                        {/* Contact & Office Information */}
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main, 
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <LocationOn fontSize="small" />
                                                Office & Consultation
                                            </Typography>
                                            <Stack spacing={2}>
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.office_location && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationOn fontSize="small" color="action" />
                                                        <strong>Office:</strong> {supervisorDetails[selectedAssignment.supervisor.id].office_location}
                                                    </Typography>
                                                )}
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.consultation_hours && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccessTime fontSize="small" color="action" />
                                                        <strong>Consultation Hours:</strong> {supervisorDetails[selectedAssignment.supervisor.id].consultation_hours}
                                                    </Typography>
                                                )}
                                                {supervisorDetails[selectedAssignment.supervisor.id]?.office_phone && (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Phone fontSize="small" color="action" />
                                                        <strong>Office Phone:</strong> {supervisorDetails[selectedAssignment.supervisor.id].office_phone}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Grid>

                                        {/* Research Expertise */}
                                        {supervisorDetails[selectedAssignment.supervisor.id]?.expertise && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: theme.palette.primary.main, 
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Science fontSize="small" />
                                                    Research Expertise & Interests
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {supervisorDetails[selectedAssignment.supervisor.id].expertise.split(',').map((exp, i) => (
                                                        <Chip 
                                                            key={i}
                                                            label={exp.trim()}
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.8rem' }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}

                                        {/* Publications & Achievements */}
                                        {supervisorDetails[selectedAssignment.supervisor.id]?.publications && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: theme.palette.primary.main, 
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Article fontSize="small" />
                                                    Selected Publications
                                                </Typography>
                                                <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {supervisorDetails[selectedAssignment.supervisor.id].publications}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}

                                        {/* Assignment Information */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main, 
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Assignment fontSize="small" />
                                                Assignment Information
                                            </Typography>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Stack spacing={1.5}>
                                                    <Typography variant="body2">
                                                        <strong>Assignment ID:</strong> {selectedAssignment.assignment_id}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Assigned Date:</strong> {formatCreatedAtDate(selectedAssignment.assigned_date)}
                                                    </Typography>
                                                    {selectedAssignment.completion_date && (
                                                        <Typography variant="body2">
                                                            <strong>Completion Date:</strong> {formatCreatedAtDate(selectedAssignment.completion_date)}
                                                        </Typography>
                                                    )}
                                                    {selectedAssignment.assignment_notes && (
                                                        <>
                                                            <Divider />
                                                            <Typography variant="body2">
                                                                <strong>Supervisor Notes:</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                                                {selectedAssignment.assignment_notes}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                                <Button 
                                    onClick={() => {
                                        handleCloseViewDialog();
                                        handleOpenChatDialog(selectedAssignment);
                                    }} 
                                    variant="outlined"
                                    startIcon={<Chat />}
                                >
                                    Send Message
                                </Button>
                                <Button onClick={handleCloseViewDialog} variant="contained">
                                    Close
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>

                {/* Chat Dialog - WhatsApp Style */}
                <Dialog
                    open={openChatDialog}
                    onClose={handleCloseChatDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            height: '80vh',
                            maxHeight: '80vh',
                            borderRadius: 3,
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        p: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                                src={getProfileImageUrl(selectedAssignment?.supervisor, 'supervisor')}
                                sx={{ width: 40, height: 40 }}
                            >
                                {getInitials(selectedAssignment?.supervisor?.name)}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {selectedAssignment?.supervisor?.name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {selectedAssignment?.supervisor?.email}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={handleCloseChatDialog}
                            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 2, flex: 1, overflowY: 'auto', bgcolor: '#f5f5f5' }} id="chat-messages">
                        {messages.length === 0 ? (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                height: '100%',
                                color: 'text.secondary'
                            }}>
                                <Chat sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                <Typography variant="body2">No messages yet</Typography>
                                <Typography variant="caption">Start a conversation with your supervisor</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.5}>
                                {messages.map((message, index) => {
                                    const isOwnMessage = message.sender_type === 'student';
                                    const showDate = index === 0 || 
                                        new Date(message.created_at).toDateString() !== new Date(messages[index - 1]?.created_at).toDateString();
                                    
                                    return (
                                        <React.Fragment key={message.id}>
                                            {showDate && (
                                                <Box sx={{ textAlign: 'center', my: 2 }}>
                                                    <Chip 
                                                        label={new Date(message.created_at).toLocaleDateString(undefined, { 
                                                            weekday: 'long', 
                                                            year: 'numeric', 
                                                            month: 'long', 
                                                            day: 'numeric' 
                                                        })}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
                                                    />
                                                </Box>
                                            )}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                                mb: 0.5
                                            }}>
                                                <Paper sx={{
                                                    maxWidth: '70%',
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: isOwnMessage ? theme.palette.primary.main : 'white',
                                                    color: isOwnMessage ? 'white' : 'text.primary',
                                                    position: 'relative'
                                                }}>
                                                    <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                                                        {message.message}
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mt: 0.5,
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                        <Typography variant="caption" sx={{
                                                            fontSize: '0.65rem',
                                                            color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                                        }}>
                                                            {message.formatted_time}
                                                        </Typography>
                                                        {isOwnMessage && (
                                                            message.is_read ? (
                                                                <DoneAll sx={{ fontSize: 12, color: '#4fc3f7' }} />
                                                            ) : (
                                                                <Done sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }} />
                                                            )
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </React.Fragment>
                                    );
                                })}
                            </Stack>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ 
                        p: 2, 
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'white'
                    }}>
                        <TextField
                            fullWidth
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            multiline
                            maxRows={3}
                            size="small"
                            InputProps={{
                                sx: { borderRadius: 3 }
                            }}
                        />
                        <IconButton 
                            color="primary" 
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || sendingMessage}
                            sx={{ 
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                '&:hover': { bgcolor: theme.palette.primary.dark },
                                '&.Mui-disabled': { bgcolor: theme.palette.grey[300] }
                            }}
                        >
                            {sendingMessage ? <CircularProgress size={24} /> : <Send />}
                        </IconButton>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default AssignedSupervisor;